"use client";

import { useState } from "react";
import { BackTile } from "./BackTile";
import { FormulaButton } from "./FormulaButton";
import type { MetricItem } from "./IntakeMetricSelect";
import { IntakeOptionCard } from "./IntakeOptionCard";

/**
 * Sandbox metric selector. Splits the ranked metric list into a small set of
 * auto-selected "recommended" metrics and a capped "more metrics" section that
 * expands to the full list via a plaintext link. A persistent final tile routes
 * back to problem selection when nothing fits.
 *
 * Tiles show the metric definition inline on a second line and keep the `fx`
 * formula chip.
 */

type Metric = MetricItem & { id: string };

export interface MetricsPickerProps {
  metrics: Metric[];
  selectedIds: string[];
  onToggle: (id: string) => void;
  /** How many leading metrics count as "recommended". Default 3. */
  recommendedCount?: number;
  /** How many additional metrics to show before "show all". Default 5. */
  moreLimit?: number;
  /** Route back to the problem-selection step. */
  onBackToProblems: () => void;
}

const SECTION_LABEL_CLS =
  "text-[11px] font-semibold uppercase tracking-[.06em] text-(--fg-muted)";

export function MetricsPicker({
  metrics,
  selectedIds,
  onToggle,
  recommendedCount = 3,
  moreLimit = 5,
  onBackToProblems,
}: MetricsPickerProps) {
  const [expanded, setExpanded] = useState(false);

  const recommended = metrics.slice(0, recommendedCount);
  const additional = metrics.slice(recommendedCount);
  const visibleAdditional = expanded
    ? additional
    : additional.slice(0, moreLimit);
  const hasMore = additional.length > moreLimit;

  return (
    <div className="flex flex-col gap-5">
      {/* biome-ignore lint/a11y/useSemanticElements: cards use ARIA checkbox roles; this is their non-form grouping container */}
      <section role="group" className="flex flex-col gap-2">
        <div className={SECTION_LABEL_CLS}>Recommended</div>
        {recommended.map((metric) => (
          <IntakeOptionCard
            key={metric.id}
            name={metric.name}
            description={metric.definition}
            indicator="checkbox"
            selected={selectedIds.includes(metric.id)}
            onSelect={() => onToggle(metric.id)}
            meta={
              metric.formula && metric.formula.length > 0 ? (
                <FormulaButton formula={metric.formula} />
              ) : undefined
            }
          />
        ))}
      </section>

      {additional.length > 0 && (
        // biome-ignore lint/a11y/useSemanticElements: cards use ARIA checkbox roles; this is their non-form grouping container
        <section role="group" className="flex flex-col gap-2">
          <div className={SECTION_LABEL_CLS}>More metrics</div>
          {visibleAdditional.map((metric) => (
            <IntakeOptionCard
              key={metric.id}
              name={metric.name}
              description={metric.definition}
              indicator="checkbox"
              selected={selectedIds.includes(metric.id)}
              onSelect={() => onToggle(metric.id)}
              meta={
                metric.formula && metric.formula.length > 0 ? (
                  <FormulaButton formula={metric.formula} />
                ) : undefined
              }
            />
          ))}
          {hasMore && (
            <button
              type="button"
              onClick={() => setExpanded((value) => !value)}
              className="mt-0.5 self-start rounded-[4px] border-0 bg-transparent px-1 py-1 font-[family-name:var(--font-body)] text-[13px] font-medium text-(--fg-brand) underline decoration-transparent underline-offset-[3px] transition-colors duration-150 hover:decoration-current"
            >
              {expanded
                ? "Show fewer metrics"
                : `Show ${additional.length} additional metrics`}
            </button>
          )}
        </section>
      )}

      <BackTile
        title="Metrics look off, or don't see yours?"
        description="Go back to problem selection."
        onClick={onBackToProblems}
      />
    </div>
  );
}
